import { PlaidSourceName } from 'features/plaid/constants/PlaidSourceNames';
import { useEffect, useState } from 'react';
import useEvent from 'shared/hooks/useEvent/useEvent';
import {
  PlaidIntent,
  PlaidIntentTypes,
  PlaidLaunchIntent,
  PlaidOpenResult,
} from 'shared/hooks/usePlaidV2/types';
import usePlaidV2 from 'shared/hooks/usePlaidV2/usePlaidV2';

const WAIT_INTENT: PlaidIntentTypes = { intent: PlaidIntent.Wait };

type UsePlaidLauncherOptions = {
  sourceName: PlaidSourceName;
  onResult: (result: PlaidOpenResult) => void;
};

/**
 * Drives a Plaid V2 flow for a single launching surface.
 *
 * The hook stays on the `Wait` intent until `startLaunch` is called, switches to
 * `activeIntent` to prepare a token, opens Plaid once the SDK is ready, and
 * hands the resolved `PlaidOpenResult` to `onResult`.
 *
 * @param activeIntent The flow to run once launched — `Link`, `ReLink`, or
 * `CraUpgrade`. The hook stays idle while this is `undefined`, which lets a
 * surface defer until it knows the account it needs to repair.
 * @param options `sourceName` is the approved analytics source for the surface
 * launching Plaid. `onResult` receives every `openPlaid()` outcome so the
 * surface can distinguish `success`, `exit`, `error`, and `blocked`.
 * @returns An object containing:
 * - `startLaunch`: Begins preparing and opening Plaid.
 * - `cancelLaunch`: Returns to idle and clears prepared Plaid state.
 * - `error`: The token error for the in-flight launch, or `null` when idle.
 */
const usePlaidLauncher = (
  activeIntent: PlaidLaunchIntent | undefined,
  { sourceName, onResult }: UsePlaidLauncherOptions,
) => {
  const [isLaunching, setIsLaunching] = useState(false);

  const launchIntent = isLaunching ? activeIntent : undefined;

  const {
    openPlaid,
    ready: isPlaidReady,
    error: plaidError,
    reset: resetPlaid,
  } = usePlaidV2(launchIntent ?? WAIT_INTENT, { sourceName });

  const error = isLaunching ? plaidError : null;

  const handleLaunch = useEvent(async () => {
    const result = await openPlaid();

    setIsLaunching(false);
    onResult(result);
  });

  const startLaunch = useEvent(() => {
    if (!activeIntent) {
      return;
    }

    setIsLaunching(true);
  });

  const cancelLaunch = useEvent(() => {
    setIsLaunching(false);
    resetPlaid();
  });

  useEffect(() => {
    if (!launchIntent || !isPlaidReady || error) {
      return;
    }

    void handleLaunch();
  }, [error, handleLaunch, isPlaidReady, launchIntent]);

  return {
    startLaunch,
    cancelLaunch,
    error,
  };
};

export default usePlaidLauncher;
